import { useMemo } from 'react';
import {
  getBatteryCapacity,
  computeChargeRate,
  estimateBatteryLifeDays,
  computeWaterDepthCm,
  computeTankVolumeLiters,
  computeVolumeChangeLiters,
  hoursBetween,
} from '@shared/metrics';
import type { TelemetryPoint, UnitConfig } from '@shared/metrics';

// Re-exported so existing importers (e.g. UnitData) keep a single import site.
export { getBatteryCapacity };

export interface Unit {
  sensorMountHeight?: number;
  tankDimensions?: { diameter?: number; height?: number };
  tankShape?: string;
  maxBatteryVoltage?: number;
}

export interface Telemetry {
  voltage: number;
  measurement: number;
  timestamp: string;
}

export type RollingWindow = '24h' | '7d' | '30d';

export interface CalculatedMetrics {
  /** null when there is no telemetry to derive it from — never conflate with a real 0. */
  batteryCapacityPercent: number | null;
  estimatedBatteryLifeDays: number | null;
  /** null when there is no telemetry to derive it from — never conflate with a real 0. */
  batteryChargeRateVperHr: number | null;
  waterDepthCm: number | null;
  waterVolumeLiters: number | null;
  volumeChangeLiters: number | null;
}

function windowMsFor(window: RollingWindow): number {
  if (window === '7d') return 7 * 24 * 60 * 60 * 1000;
  if (window === '30d') return 30 * 24 * 60 * 60 * 1000;
  return 24 * 60 * 60 * 1000;
}

/** Pick the newest point at/older than `targetMs`, falling back to the oldest. */
function pointAtOrBefore(sortedDesc: Telemetry[], targetMs: number): Telemetry {
  for (let i = 0; i < sortedDesc.length; i++) {
    if (new Date(sortedDesc[i].timestamp).getTime() <= targetMs) return sortedDesc[i];
  }
  return sortedDesc[sortedDesc.length - 1];
}

export function useMetricsCalculator(
  unit: Unit | undefined | null,
  telemetry: Telemetry[],
  volumeWindow: RollingWindow = '24h',
): CalculatedMetrics {
  return useMemo(() => {
    const defaultMetrics: CalculatedMetrics = {
      batteryCapacityPercent: null,
      estimatedBatteryLifeDays: null,
      batteryChargeRateVperHr: null,
      waterDepthCm: null,
      waterVolumeLiters: null,
      volumeChangeLiters: null,
    };

    if (!unit || telemetry.length === 0) return defaultMetrics;

    const config: UnitConfig = unit;
    const maxV = unit.maxBatteryVoltage || 4.2;

    // Newest first.
    const sorted = [...telemetry].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    const latest = sorted[0];
    const now = new Date(latest.timestamp).getTime();
    const latestPoint: TelemetryPoint = {
      voltage: latest.voltage,
      measurement: latest.measurement,
      timestamp: latest.timestamp,
    };

    const capacity = getBatteryCapacity(latest.voltage, maxV);

    // Charge rate & estimated life: compare against a point ~24h back.
    let chargeRate = 0;
    let estDays: number | null = null;
    const ratePrev = pointAtOrBefore(sorted, now - 24 * 60 * 60 * 1000);
    if (ratePrev && ratePrev.timestamp !== latest.timestamp) {
      const prevPoint: TelemetryPoint = {
        voltage: ratePrev.voltage,
        measurement: ratePrev.measurement,
        timestamp: ratePrev.timestamp,
      };
      chargeRate = computeChargeRate(latestPoint, prevPoint);
      const prevCapacity = getBatteryCapacity(ratePrev.voltage, maxV);
      estDays = estimateBatteryLifeDays(capacity, prevCapacity, hoursBetween(latest.timestamp, ratePrev.timestamp));
    }

    const waterDepth = computeWaterDepthCm(latest.measurement, config);
    const waterVolume = waterDepth === null ? null : computeTankVolumeLiters(waterDepth, config);

    // Volume change over the selected rolling window.
    let volumeChange: number | null = null;
    if (waterVolume !== null) {
      const volPrev = pointAtOrBefore(sorted, now - windowMsFor(volumeWindow));
      const pastDepth = computeWaterDepthCm(volPrev.measurement, config);
      const pastVolume = pastDepth === null ? null : computeTankVolumeLiters(pastDepth, config);
      if (pastVolume !== null) volumeChange = computeVolumeChangeLiters(waterVolume, pastVolume);
    }

    return {
      batteryCapacityPercent: capacity,
      estimatedBatteryLifeDays: estDays,
      batteryChargeRateVperHr: chargeRate,
      waterDepthCm: waterDepth,
      waterVolumeLiters: waterVolume,
      volumeChangeLiters: volumeChange,
    };
  }, [unit, telemetry, volumeWindow]);
}
